<template>
  <rest-page ref="restPage" :fetch-endpoint="fetchEndpoint">
    <template v-if="model">
      <rest-form
        ref="usersForm"
        :model="usersSaveModel"
        :save-endpoint="saveEndpoint"
        :after-save="afterUsersSave"
        :success-message="$t('DataRetention_SaveSuccess')"
        :submit-native="false"
        @on-error="onUsersSaveError"
      >
        <div class="policy-card">
          <div class="policy-card-header">
            <div>
              <h3 class="policy-title">
                {{ $t('DataRetention_Users_Title') }}
              </h3>
              <p class="policy-description">
                {{ $t('DataRetention_Users_Description') }}
              </p>
            </div>
            <div class="section-controls">
              <el-button
                v-if="editingSection !== 'users' && canManage"
                type="primary"
                data-qa="data-retention-users-edit"
                @click="startEditUsers"
              >
                {{ $t('Button_Edit') }}
              </el-button>
              <template v-else-if="editingSection === 'users' && canManage">
                <el-button
                  data-qa="data-retention-users-cancel"
                  @click="cancelEdit"
                  >{{ $t('Button_Cancel') }}</el-button
                >
                <el-button
                  type="primary"
                  data-qa="data-retention-users-save"
                  @click="saveUsers"
                  >{{ $t('Button_Save') }}</el-button
                >
              </template>
            </div>
          </div>

          <div class="policy-grid">
            <rest-form-item
              v-for="setting in userSettings"
              :key="setting.field"
              :prop="setting.field"
              class="policy-grid-item"
            >
              <template #label>{{ $t(setting.label) }}</template>
              <div class="toggle-row">
                <toggle
                  :active-value="true"
                  :inactive-value="false"
                  v-model="usersToggles[setting.field]"
                  :disabled="editingSection !== 'users'"
                  @change="
                    editingSection === 'users' &&
                      onToggleField(usersModel, setting.field, $event, MIN_DAYS)
                  "
                />
                <span class="status-text">
                  {{
                    usersToggles[setting.field]
                      ? $t('DataRetention_Status_Enabled')
                      : $t('DataRetention_Status_Disabled')
                  }}
                </span>
              </div>
              <transition name="expand">
                <div v-if="usersToggles[setting.field]" class="field-wrap">
                  <div class="input-field-block">
                    <template v-if="editingSection === 'users'">
                      <div class="inline-field">
                        <el-input-number
                          v-model="usersModel[setting.field]"
                          :controls="true"
                          controls-position="right"
                        />
                        <span class="unit">{{ $t('DataRetention_Days') }}</span>
                      </div>
                    </template>
                    <div v-else class="static-value">
                      {{ formatDays(usersModel[setting.field]) }}
                    </div>
                  </div>
                </div>
              </transition>
              <div v-if="!usersToggles[setting.field]" class="helper-text">
                {{ $t('DataRetention_CleanupDisabled') }}
              </div>
            </rest-form-item>
          </div>
        </div>
      </rest-form>

      <rest-form
        ref="auditForm"
        :model="auditSaveModel"
        :save-endpoint="saveEndpoint"
        :after-save="afterAuditSave"
        :success-message="$t('DataRetention_SaveSuccess')"
        :submit-native="false"
      >
        <div class="policy-card">
          <div class="policy-card-header">
            <div>
              <h3 class="policy-title">
                {{ $t('DataRetention_AuditLog_Title') }}
              </h3>
              <p class="policy-description">
                {{ $t('DataRetention_AuditLog_Description') }}
              </p>
            </div>
            <div class="section-controls">
              <el-button
                v-if="editingSection !== 'audit' && canManage"
                type="primary"
                data-qa="data-retention-audit-edit"
                @click="startEditAudit"
              >
                {{ $t('Button_Edit') }}
              </el-button>
              <template v-else-if="editingSection === 'audit' && canManage">
                <el-button
                  data-qa="data-retention-audit-cancel"
                  @click="cancelEdit"
                  >{{ $t('Button_Cancel') }}</el-button
                >
                <el-button
                  type="primary"
                  data-qa="data-retention-audit-save"
                  @click="saveAudit"
                  >{{ $t('Button_Save') }}</el-button
                >
              </template>
            </div>
          </div>
          <div class="policy-grid">
            <rest-form-item
              v-for="setting in auditSettings"
              :key="setting.field"
              :prop="setting.field"
              class="policy-grid-item"
            >
              <template #label>{{ $t(setting.label) }}</template>
              <div class="toggle-row">
                <toggle
                  :active-value="true"
                  :inactive-value="false"
                  v-model="auditToggles[setting.field]"
                  :disabled="editingSection !== 'audit'"
                  @change="
                    editingSection === 'audit' &&
                      onToggleField(
                        auditModel,
                        setting.field,
                        $event,
                        MIN_MONTHS
                      )
                  "
                />
                <span class="status-text">
                  {{
                    auditToggles[setting.field]
                      ? $t('DataRetention_Status_Enabled')
                      : $t('DataRetention_Status_Disabled')
                  }}
                </span>
              </div>
              <transition name="expand">
                <div v-if="auditToggles[setting.field]" class="field-wrap">
                  <div class="input-field-block">
                    <template v-if="editingSection === 'audit'">
                      <div class="inline-field">
                        <el-input-number
                          v-model="auditModel[setting.field]"
                          :controls="true"
                          controls-position="right"
                        />
                        <span class="unit">{{
                          $t('DataRetention_Months')
                        }}</span>
                      </div>
                    </template>
                    <div v-else class="static-value">
                      {{ formatMonths(auditModel[setting.field]) }}
                    </div>
                  </div>
                </div>
              </transition>
              <div v-if="!auditToggles[setting.field]" class="helper-text">
                {{ $t('DataRetention_CleanupDisabled') }}
              </div>
            </rest-form-item>
          </div>
        </div>
      </rest-form>
    </template>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import RestFormItem from '@/common/components/rest/RestFormItem'
import { projectEndpoint } from '@/core/helpers'
import Toggle from '@/common/components/form/Toggle.vue'

const POLICY_TYPE_USER_PROFILES = 'user_profiles'
const POLICY_TYPE_AUDIT_LOGS = 'audit_logs'
const MIN_DAYS = 14
const MIN_MONTHS = 1

const USER_SETTINGS = [
  {
    field: 'uncontactable_web_devices_retention_period',
    label: 'DataRetention_Field_UncontactableWebDevicesRetentionPeriod'
  },
  {
    field: 'uncontactable_anonymous_profiles_retention_period',
    label: 'DataRetention_Field_UncontactableAnonymousProfilesRetentionPeriod'
  },
  {
    field: 'uncontactable_identified_profiles_retention_period',
    label: 'DataRetention_Field_UncontactableIdentifiedProfilesRetentionPeriod'
  }
]

const AUDIT_SETTINGS = [
  {
    field: 'profile_auditlog_retention_period_months',
    label: 'DataRetention_Field_ProfileAuditLog'
  },
  {
    field: 'campaign_auditlog_retention_period_months',
    label: 'DataRetention_Field_CampaignAuditLog'
  }
]

const initFieldMap = (settings, value) =>
  settings.reduce((acc, { field }) => {
    acc[field] = value
    return acc
  }, {})

export default {
  name: 'DataRetentionPoliciesTab',
  components: { Toggle, RestForm, RestFormItem, RestPage },
  data() {
    return {
      MIN_DAYS,
      MIN_MONTHS,
      model: null,
      editingSection: null,
      usersModel: initFieldMap(USER_SETTINGS, null),
      usersToggles: initFieldMap(USER_SETTINGS, false),
      usersErrors: {},
      auditModel: initFieldMap(AUDIT_SETTINGS, null),
      auditToggles: initFieldMap(AUDIT_SETTINGS, false),
      usersSaveModel: {
        policy_type: POLICY_TYPE_USER_PROFILES,
        profile_retention: 0,
        ...initFieldMap(USER_SETTINGS, null)
      },
      auditSaveModel: {
        policy_type: POLICY_TYPE_AUDIT_LOGS,
        audit_log_retention: 0,
        ...initFieldMap(AUDIT_SETTINGS, null)
      }
    }
  },
  computed: {
    canManage() {
      return this.$can('DataRetention:ManageAccess')
    },
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'retention-policy',
        handler: data => {
          this.model = data
          this.syncSectionModels()
        }
      })
    },
    saveEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: 'retention-policy'
      })
    },
    userSettings() {
      return USER_SETTINGS
    },
    auditSettings() {
      return AUDIT_SETTINGS
    }
  },
  methods: {
    buildPayload(sectionModel, toggles, settings, policyType, masterField) {
      const payload = { policy_type: policyType }
      if (masterField) {
        payload[masterField] = settings.some(({ field }) => toggles[field])
          ? 1
          : 0
      }
      settings.forEach(({ field }) => {
        payload[field] = toggles[field] ? Number(sectionModel[field]) : null
      })
      return payload
    },
    clearFormErrors(refName) {
      if (this.$refs[refName]) {
        this.$refs[refName].errors = []
      }
    },
    setFormErrors(refName, errors) {
      if (this.$refs[refName]) {
        this.$refs[refName].errors = errors
      }
    },
    afterUsersSave(data, finishLoading) {
      this.model = data
      this.editingSection = null
      this.usersErrors = {}
      this.clearFormErrors('usersForm')
      this.syncSectionModels()
      finishLoading()
    },
    afterAuditSave(data, finishLoading) {
      this.model = data
      this.editingSection = null
      this.clearFormErrors('auditForm')
      this.syncSectionModels()
      finishLoading()
    },
    onUsersSaveError(error) {
      const errors =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
          ? error.response.data.errors
          : []
      const profileRetentionError = Array.isArray(errors)
        ? errors.find(item => item && item.field === 'profile_retention')
        : null

      this.usersErrors = profileRetentionError
        ? { general: [profileRetentionError.message] }
        : {}
    },
    syncSection(sectionModel, toggles, settings) {
      settings.forEach(({ field }) => {
        const value = this.model[field]
        sectionModel[field] = value
        toggles[field] = value != null
      })
    },
    syncSectionModels() {
      this.syncSection(this.usersModel, this.usersToggles, USER_SETTINGS)
      this.syncSection(this.auditModel, this.auditToggles, AUDIT_SETTINGS)
    },
    startEditUsers() {
      this.syncSectionModels()
      this.usersErrors = {}
      this.clearFormErrors('usersForm')
      this.editingSection = 'users'
    },
    startEditAudit() {
      this.syncSectionModels()
      this.clearFormErrors('auditForm')
      this.editingSection = 'audit'
    },
    cancelEdit() {
      this.editingSection = null
      this.usersErrors = {}
      this.clearFormErrors('usersForm')
      this.clearFormErrors('auditForm')
      this.syncSectionModels()
    },
    onToggleField(sectionModel, field, enabled, minValue) {
      if (!enabled) {
        sectionModel[field] = null
      } else if (sectionModel[field] == null) {
        sectionModel[field] = minValue
      }
    },
    validateUsers() {
      const errors = {}

      USER_SETTINGS.forEach(({ field }) => {
        if (!this.usersToggles[field]) {
          this.usersModel[field] = null
          return
        }
        const value = Number(this.usersModel[field])
        if (Number.isNaN(value)) {
          errors[field] = [this.$t('Validation_FieldIsRequired')]
          return
        }
        if (value < MIN_DAYS) {
          errors[field] = [
            this.$t('DataRetention_Validation_DaysMin', { min: MIN_DAYS })
          ]
        }
      })

      this.setFormErrors(
        'usersForm',
        USER_SETTINGS.reduce((acc, { field }) => {
          acc[field] = errors[field] || null
          return acc
        }, {})
      )
      return Object.keys(errors).length === 0
    },
    validateMonthsField(value, enabled) {
      if (!enabled) {
        return null
      }
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) {
        return [this.$t('Validation_FieldIsRequired')]
      }
      if (numberValue < MIN_MONTHS) {
        return [
          this.$t('DataRetention_Validation_MonthsMin', { min: MIN_MONTHS })
        ]
      }
      return null
    },
    validateAudit() {
      const errors = {}
      let hasError = false
      AUDIT_SETTINGS.forEach(({ field }) => {
        const fieldError = this.validateMonthsField(
          this.auditModel[field],
          this.auditToggles[field]
        )
        if (fieldError) {
          errors[field] = fieldError
          hasError = true
        }
      })
      this.setFormErrors('auditForm', errors)
      return !hasError
    },
    formatDays(value) {
      if (value == null || value === '') {
        return this.$t('DataRetention_CleanupDisabled')
      }
      return `${Number(value)} ${this.$t('DataRetention_Days')}`
    },
    formatMonths(value) {
      if (value == null || value === '') {
        return this.$t('DataRetention_CleanupDisabled')
      }
      return `${Number(value)} ${this.$t('DataRetention_Months')}`
    },
    async saveUsers() {
      if (!this.validateUsers()) {
        return
      }
      Object.assign(
        this.usersSaveModel,
        this.buildPayload(
          this.usersModel,
          this.usersToggles,
          USER_SETTINGS,
          POLICY_TYPE_USER_PROFILES,
          'profile_retention'
        )
      )
      await this.$refs.usersForm.save()
    },
    async saveAudit() {
      if (!this.validateAudit()) {
        return
      }
      Object.assign(
        this.auditSaveModel,
        this.buildPayload(
          this.auditModel,
          this.auditToggles,
          AUDIT_SETTINGS,
          POLICY_TYPE_AUDIT_LOGS,
          'audit_log_retention'
        )
      )
      await this.$refs.auditForm.save()
    }
  }
}
</script>

<style lang="scss" scoped>
.policy-card {
  border: 1px solid #dbdee7;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  background: #fff;
}

.policy-card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.policy-title {
  margin: 0 0 6px;
  font-size: 22px;
  line-height: 28px;
  color: #4b515f;
  font-weight: 600;
}

.policy-description {
  margin: 0;
  font-size: 14px;
  color: #5c6374;
  max-width: 760px;
}

.section-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.policy-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.policy-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #4b515f;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.status-text {
  color: #7e8596;
  font-size: 14px;
}

.inline-field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.unit {
  color: #5c6374;
  font-size: 14px;
}

.helper-text {
  color: #7e8596;
  font-size: 14px;
}

.field-wrap {
  margin-top: 6px;
}

.input-field-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.static-value {
  color: #5c6374;
  font-size: 14px;
}

.inline-error {
  color: #f56c6c;
  font-size: 13px;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

@media (max-width: 1360px) {
  .policy-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .policy-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/components/DataRetentionPoliciesTab.vue